import { faArrowLeft, faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { UserfulTooltip } from "userful-chronos-common-ui/dist";
import {
    KeycloakGroup,
    KeycloakUser,
    UpdateKeycloakUser,
    UserfulGroup,
    UserfulUser,
} from "../../model/CarbonExternalModels";
import { selectUserGroups, selectUsers, useCarbonDispatch, useCarbonSelector } from "../../store/carbonStore";
import { AppSliceActions } from "../../store/app/appSlice";
import UserDropdown from "./UserDropdown";
import { getGlobalStates } from "userful-chronos-app-common-js/dist/globalstates/globalStates";
import { GcmUserPermission } from "userful-chronos-app-common-js/dist/models/gcm-user-permission";
import UserDelete from "./userDelete/UserDelete";
import { useState, useEffect } from "react";
import { objectEqual } from "../../common/functions/objectComparison";
import { apiUpdateUser } from "../../messages/api/gcmUserManagement";
import { initKeycloakUser } from "../../store/userManagement/userManagementUtils";
import { isEmailValid } from "../../common/functions/password";
import UserGroupMultiselect from "../userGroups/userGroupMultiselect/UserGroupMultiselect";

interface IProps {
    user: KeycloakUser;
}

export default function UserPage(props: IProps) {
    const dispatch = useCarbonDispatch();

    const permissions: GcmUserPermission = getGlobalStates().gcmUserPermission;

    const [originalUser, setOriginalUser] = useState<KeycloakUser>(props.user ? props.user : initKeycloakUser());
    const [currentUser, setCurrentUser] = useState<KeycloakUser>(originalUser);

    const canChange = props.user.userProfile.userName !== "admin" && permissions.isUserCreator;

    // Data
    const users: { [id: string]: UserfulUser } = useCarbonSelector(selectUsers);
    const userGroups: { [id: string]: UserfulGroup } = useCarbonSelector(selectUserGroups);

    const allUsers: KeycloakUser[] = [];
    const allUserGroups: KeycloakGroup[] = [];

    for (const property in users) {
        allUsers.push(users[property].user);
    }
    for (const property in userGroups) {
        allUserGroups.push(userGroups[property].group);
    }

    const hasPermissionsSet = allUserGroups.length > 1;

    let nameError =
        allUsers.find((c) => c.userProfile.userName === currentUser.userProfile.userName) &&
        currentUser.userProfile.userName !== originalUser.userProfile.userName
            ? "Please make sure the name is unique."
            : "";
    const userGroupError = currentUser.groups.length >= 1 ? "" : "Please make sure user belongs to at least one group.";
    const emailError =
        !(
            allUsers.find((c) => c.userProfile.email === currentUser.userProfile.email) &&
            currentUser.userProfile.email !== originalUser.userProfile.email
        ) && isEmailValid(currentUser.userProfile.email)
            ? ""
            : "Please make sure to enter a valid and unique email address.";

    useEffect(() => {
        setOriginalUser(props.user ? props.user : initKeycloakUser());
        setCurrentUser(props.user ? props.user : initKeycloakUser());
    }, [props]);

    let hasChanges =
        !objectEqual(originalUser, currentUser) &&
        nameError.length === 0 &&
        emailError.length === 0 &&
        userGroupError.length === 0;

    function handleNameChange(event) {
        setCurrentUser({ ...currentUser, userProfile: { ...currentUser.userProfile, userName: event.target.value } });
    }
    function handleFirstNameChange(event) {
        setCurrentUser({ ...currentUser, userProfile: { ...currentUser.userProfile, firstName: event.target.value } });
    }
    function handleLastNameChange(event) {
        setCurrentUser({ ...currentUser, userProfile: { ...currentUser.userProfile, lastName: event.target.value } });
    }
    function handleEmailChange(event) {
        setCurrentUser({ ...currentUser, userProfile: { ...currentUser.userProfile, email: event.target.value } });
    }
    function onUserGroupsChange(data) {
        if (canChange) setCurrentUser({ ...currentUser, ...data });
    }
    const onDiscardChanges = (e) => {
        setCurrentUser(originalUser);
    };
    const onSaveChanges = (e) => {
        const updateKeycloakUser: UpdateKeycloakUser = {
            id: currentUser.id,
            enableMfa: currentUser.mfaEnable,
            groups: currentUser.groups,
            userProfile: currentUser.userProfile,
        };

        console.warn(updateKeycloakUser);

        apiUpdateUser(getGlobalStates().keycloak.token, updateKeycloakUser);
    };

    if (!props.user) return <div className="genericWrapper">User not found</div>;
    return (
        <div className="genericWrapper">
            <div className="actionsBar">
                <div className="cluster normalGap">
                    <UserfulTooltip title={"Return to Users"}>
                        <button
                            className="iconButton"
                            onClick={(e) => {
                                dispatch(AppSliceActions.setAppView({ type: "USERS", id: { value: "NOT_DEFINED" } }));
                            }}
                            id="user-go-back-button"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                        </button>
                    </UserfulTooltip>
                    <UserDropdown user={props.user} />
                </div>

                <div className="cluster normalGap">
                    <UserDelete type="ICON" user={props.user} permissions={permissions} />
                </div>
            </div>
            <div className="gcm-page-wrapper">
                <div className="gcm-form-Body">
                    <div className="gcm-form-Header">
                        <div className="gcm-form-Row">
                            <h1 className="gcm-form-HeaderTitle">User Details</h1>
                        </div>
                        <div className="gcm-form-Row">
                            <h3 className="gcm-form-HeaderDescription">
                                User is a person who can log in to the system and perform actions based on the
                                permissions assigned to them.
                            </h3>
                        </div>
                    </div>
                    <div className="gcm-form-Contents">
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <h4 className="gcm-form-ContentsTitle">User Details</h4>
                            <h5 className="gcm-form-ContentsDescription">Details relating to this user.</h5>
                        </div>
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <div className="gcm-form-FieldTitle">
                                <h5>Username</h5>
                                <h6>{currentUser.userProfile.userName.length}/60</h6>
                            </div>
                            <input
                                disabled={props.user ? true : false}
                                className="userfulInputField"
                                placeholder="Enter name here..."
                                type="text"
                                maxLength={60}
                                value={currentUser.userProfile.userName}
                                onChange={handleNameChange}
                            />
                            {nameError && <div className="errorText">{nameError}</div>}
                        </div>
                        <div className="gcm-form-Column gcm-form-NormalGap">
                            <div className="fieldRowWrapper">
                                <div className="gcm-form-Column gcm-form-SmallGap">
                                    <div className="gcm-form-FieldTitle">
                                        <h5>First Name</h5>
                                        {/* <h6>{currentUser.userProfile.firstName.length}/60</h6> */}
                                    </div>
                                    <input
                                        disabled={!canChange}
                                        className="userfulInputField"
                                        placeholder="Enter first name here..."
                                        type="text"
                                        maxLength={60}
                                        value={
                                            currentUser.userProfile.firstName ? currentUser.userProfile.firstName : ""
                                        }
                                        onChange={handleFirstNameChange}
                                    />
                                </div>
                                <div className="gcm-form-Column gcm-form-SmallGap">
                                    <div className="gcm-form-FieldTitle">
                                        <h5>Last Name</h5>
                                        {/* <h6>{currentUser.userProfile.firstName.length}/60</h6> */}
                                    </div>
                                    <input
                                        disabled={!canChange}
                                        className="userfulInputField"
                                        placeholder="Enter last name here..."
                                        type="text"
                                        maxLength={60}
                                        value={currentUser.userProfile.lastName ? currentUser.userProfile.lastName : ""}
                                        onChange={handleLastNameChange}
                                    />
                                </div>
                            </div>
                        </div>
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <div className="gcm-form-FieldTitle">
                                <h5>Email Address</h5>
                            </div>
                            <input
                                disabled={!canChange}
                                className="userfulInputField"
                                placeholder="Enter email address here..."
                                type="email"
                                maxLength={200}
                                value={currentUser.userProfile.email ? currentUser.userProfile.email : ""}
                                onChange={handleEmailChange}
                            />
                            {emailError && <div className="errorText">{emailError}</div>}
                        </div>
                    </div>
                    <div className="gcm-form-Contents">
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <h4 className="gcm-form-ContentsTitle">Permission sets</h4>
                            <h5 className="gcm-form-ContentsDescription">
                                Assign this user to one or multiple permission sets.
                            </h5>
                        </div>
                        <div className="gcm-form-Column gcm-form-SmallGap">
                            <div className="gcm-form-FieldTitle">
                                <h5>Permission sets</h5>
                            </div>
                            <UserGroupMultiselect user={currentUser} onChange={onUserGroupsChange} />
                            {userGroupError && <div className="errorText">{userGroupError}</div>}
                        </div>
                    </div>
                </div>
            </div>
            <div className="gcm-form-Footer">
                <button className="secondaryUserfulButton" disabled={!hasChanges} onClick={onDiscardChanges}>
                    Discard Changes
                </button>
                <button className="primaryUserfulButton" disabled={!hasChanges} onClick={onSaveChanges}>
                    Save Changes
                </button>
            </div>
        </div>
    );
}
